import { useCallback, useMemo } from "react";

import { type AttributionFilter } from "@doitintl/cmp-models";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";

import { getModeLabel } from "../../../../../../../Components/Dialogs/CloudAnalytics/utils";
import AllocationRuleChips from "../../../../../../../Components/DimensionSelectorWithOperator/AllocationRuleChips";
import { useCloudAnalyticsMetadata } from "../../../../../../../Components/hooks/cloudAnalytics/useCloudAnalyticsMetadata";
import { type MetadataOption } from "../../../../../../../types";
import { getLetterForIndex } from "../../../../../../../utils/string";
import { FilterModeOption } from "../../../../../allocations/types";
import { DRAWER_TYPES } from "../../../constants";
import { useDrawerStore } from "../../../hooks/useDrawerStore";

const isAllocationData = (data: unknown, type: string | null): data is MetadataOption =>
  type === DRAWER_TYPES.ALLOCATION && data !== null && typeof data === "object" && "data" in data;

type FilterProps = { filters: AttributionFilter[] };

const Filters = ({ filters }: FilterProps) => {
  const { data, type } = useDrawerStore();
  const allocationData = isAllocationData(data, type) ? data : null;
  const variablesArray = useMemo(
    (): string[] => filters?.map((_: unknown, index: number): string => getLetterForIndex(index)) || [],
    [filters]
  );
  const { metadata } = useCloudAnalyticsMetadata();

  const getMode = useCallback((filter, reverse) => {
    if (filter.regexp) {
      return reverse ? FilterModeOption.DOES_NOT_MATCHES_REGEX : FilterModeOption.MATCHES_REGEX;
    }
    return getModeLabel(filter.mode, filter.inverse);
  }, []);

  const getFilterDimension = useCallback(
    (filter: AttributionFilter): string => {
      const md = metadata.find((md) => md.id === filter.id);
      return md?.get("label") ?? filter.field;
    },
    [metadata]
  );

  return (
    <Stack gap={1}>
      {filters.map((filter, index) => {
        const dimensionLabel = getFilterDimension(filter);
        const modeLabel = getMode(filter, filter.inverse);
        return (
          <Stack
            direction="row"
            key={filter.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              minWidth: 0, // Allow flex items to shrink below their content size
              overflow: "hidden",
            }}
          >
            <Chip
              label={variablesArray[index]}
              sx={{ flexShrink: 0 }} // Prevent variable chip from shrinking
            />
            <Tooltip title={dimensionLabel} placement="top">
              <Typography
                variant="body1"
                sx={{
                  minWidth: 120,
                  maxWidth: 200,
                  fontWeight: 500,
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  flexShrink: 1,
                }}
              >
                {dimensionLabel}
              </Typography>
            </Tooltip>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                maxWidth: 150,
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                flexShrink: 1,
              }}
            >
              {modeLabel}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                minWidth: 0,
                flex: 1,
                overflow: "hidden",
              }}
            >
              {filter.regexp ? (
                <Tooltip title={filter.regexp} placement="bottom">
                  <Chip
                    label={filter.regexp}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{
                      maxWidth: "100%",
                      "& .MuiChip-label": {
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        maxWidth: "300px",
                      },
                    }}
                  />
                </Tooltip>
              ) : (
                <Box sx={{ minWidth: 0, overflow: "hidden", flex: 1 }}>
                  <AllocationRuleChips
                    filter={filter}
                    ruleFilterWidth={100}
                    nullFallback={null}
                    title={allocationData?.data.label ?? ""}
                  />
                </Box>
              )}
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default Filters;
